import { useState, useRef } from "react";
import { FormInstance } from "antd";
import {
  PaymentInstrumentValidationData,
  SaveCreditCardResponse,
  WandaPayment,
} from "../types/PaymentTypes";

import { useTripsCtx } from "../context/TripDetailsContext";
import {
  deletePaymentInstrument,
  savePaymentInstrument,
  validatePaymentInstrumentByPaymentInstrumentId,
} from "../services/PaymentService/PaymentInstrumentService";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";

export interface CreditCardFormRef {
  handleAddressReset: () => void;
}

export type IsValidCCStateType = {
  value: boolean;
  set: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useCreditCardActions = (form: FormInstance) => {
  const [isValidCC, setIsValidCC] = useState<boolean>(true);
  const [savedCreditCardDetails, setSavedCreditCardDetails] = useState<
    SaveCreditCardResponse
  >();
  const [shouldDisableButton, setShouldDisableButton] = useState<boolean>(true);

  const creditCardFormRef = useRef<CreditCardFormRef>(null);

  const { caregiverSummaryInfo, isWandaConcierge } = usePersonSummaryCtx();

  const validateAndCheckForm = async () => {
    const values = form.getFieldsValue();
    const { address2, middleName, ...rest } = values;

    const isEmptyFieldPresent = Object.values(rest).some(
      (value) => value === undefined || value === ""
    );

    if (isEmptyFieldPresent) {
      setShouldDisableButton(true);
      return;
    }

    try {
      await form.validateFields();
      setShouldDisableButton(false);
    } catch (error: any) {
      error.outOfDate ? validateAndCheckForm() : setShouldDisableButton(true);
    }
  };

  const constructCreditCardDetails = (): PaymentInstrumentValidationData => {
    const {
      firstName,
      middleName,
      lastName,
      address1,
      address2,
      city,
      state,
      zipcode,
      cardNumber,
      expiryDate,
      cvv,
    } = form.getFieldsValue();

    return {
      personId: isWandaConcierge ? caregiverSummaryInfo.personId : undefined,
      instrumentType: WandaPayment.CreditCard,
      gatewayType: WandaPayment.CyberSource,
      customer: {
        firstName,
        middleName,
        lastName,
        email: caregiverSummaryInfo.wandaEmail,
      },
      billTo: { address1, address2, city, state, zip: zipcode },
      card: {
        number: cardNumber,
        expirationYear: Number("20" + expiryDate.split("/")[1]),
        expirationMonth: Number(expiryDate.split("/")[0]),
        securityCode: cvv,
      },
    };
  };

  const validateCreditCardByPaymentInstrumentId = async (
    paymentInstrumentId: number
  ) => {
    try {
      const {
        data: ccValidationResult,
      } = await validatePaymentInstrumentByPaymentInstrumentId(
        paymentInstrumentId, caregiverSummaryInfo.personId
      );
      return ccValidationResult.isValid;
    } catch (error) {
      throw error;
    }
  };

  const saveCreditCard = async (isDefault: boolean) => {
    try {
      await form.validateFields();

      const creditCardDetails = constructCreditCardDetails();

      const validCreditCardDetails = {
        isDefault,
        ...creditCardDetails,
      };
      const { data, status } = await savePaymentInstrument(
        validCreditCardDetails
      );
      if (status === 200) {
        setSavedCreditCardDetails(data);
      } else {
        setIsValidCC(false);
        throw new Error("Failed to Validate & Save Credit Card");
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  const deleteCreditCard = async (paymentInstrumentId: number) => {
    try {
      await deletePaymentInstrument(paymentInstrumentId, caregiverSummaryInfo.personId);
    } catch (error) {
      throw error;
    }
  };

  const resetFormAndSetDefaults = () => {
    form.resetFields();
    creditCardFormRef.current?.handleAddressReset();
    setIsValidCC(true);
    setShouldDisableButton(true);
  };

  const isValidCCState: IsValidCCStateType = {
    value: isValidCC,
    set: setIsValidCC,
  };

  return {
    isValidCCState,
    creditCardFormRef,
    savedCreditCardDetails,
    shouldDisableButton,
    validateAndCheckForm,
    saveCreditCard,
    deleteCreditCard,
    resetFormAndSetDefaults,
    validateCreditCardByPaymentInstrumentId,
  };
};
